// Port of `SceneFacts` and `Continuity.scenes` (SuiteStory/Continuity.swift): what the checks need to know about each
// of the manuscript's scenes. Story time is wall-clock time at the setting: it's stored as that clock reading in the zone
// it was entered in (the device's), so the moment it names is the same reading on the setting's clock.
import { StandardField, type OpenedProject } from '../project.ts';
import type { StoryEntity } from '../model/documents.ts';
import { manuscriptKind } from '../edit/storeEditor.ts';
import { explicitPresence, presence, reference, type Presence } from '../presence.ts';
import { deviceZone, formatDate, reinterpret, zoneNamed, type DateStyle, type TimeStyle, type Zone } from './zones.ts';

export interface SceneFacts {
  id: string;
  title: string;
  /** The stored text (the prose dialect, or a screenplay's Fountain). */
  text: string;
  /** The story time as stored (a clock reading in `entryTimeZone`), ms since 1970; null when untimed. */
  start: number | null;
  hours: number;
  setting: StoryEntity | null;
  presence: Presence[];
  /** The beat it's assigned to on the beat sheet (the Beat field), if any. */
  beatID: string | null;
  /** The zone story times were entered in (the device's). */
  entryTimeZone: Zone;
}

/** The clock at the setting: its time zone, local mean time from its longitude, or (where the Swift uses the device's
 *  zone) the entry zone — the same zone in the app, where story times are entered on this device. */
export function sceneZone(s: SceneFacts): Zone {
  const named = zoneNamed(s.setting?.timeZoneID);
  if (named) return named;
  if (s.setting?.longitude !== undefined) return { secondsFromGMT: Math.trunc((s.setting.longitude / 15) * 3600) };
  return s.entryTimeZone;
}

/** `SceneFacts.moment`: the instant the story time names, on the setting's clock. */
export function sceneMoment(s: SceneFacts): number | null {
  return s.start === null ? null : reinterpret(s.start, s.entryTimeZone, sceneZone(s));
}

/** `SceneFacts.end`: the moment plus its duration (at least a quarter of an hour). */
export function sceneEnd(s: SceneFacts): number | null {
  const m = sceneMoment(s);
  return m === null ? null : m + Math.max(s.hours, 0.25) * 3_600_000;
}

/** `SceneFacts.formatted`: a date in the setting's clock, for messages. */
export function formatInScene(s: SceneFacts, ms: number, date: DateStyle, time: TimeStyle, locale?: string): string {
  return formatDate(ms, sceneZone(s), date, time, locale);
}

export interface SceneFactsOptions {
  /** The zone story times were entered in; default the runtime's. */
  entryTimeZone?: Zone;
}

/** `Continuity.scenes`: the manuscript's scenes (prose, or a screenplay's Fountain; a folder only when it has text of
 *  its own) with their story data, in reading order. */
export function sceneFacts(project: OpenedProject, options: SceneFactsOptions = {}): SceneFacts[] {
  const entities = project.record.entities;
  const entryTimeZone = options.entryTimeZone ?? deviceZone();
  const out: SceneFacts[] = [];
  for (const scene of project.scenes([manuscriptKind(project.record.projectType)])) {
    const f = project.fragmentRecord(scene.id);
    if (!f) continue;
    if (scene.isFolder && f.text === '') continue;
    const start = f.metadata[StandardField.storyTime];
    const duration = f.metadata[StandardField.storyDuration];
    const settingID = reference(f.metadata, StandardField.setting);
    out.push({
      id: scene.id, title: f.title, text: f.text,
      start: start?.type === 'date' ? start.value.getTime() : null,
      hours: duration?.type === 'number' ? duration.value : 1,
      setting: settingID === null ? null : (entities.find((e) => e.id === settingID) ?? null),
      presence: presence(f.text, reference(f.metadata, StandardField.pov), explicitPresence(f.presence), entities, f.kind),
      beatID: reference(f.metadata, StandardField.beat),
      entryTimeZone,
    });
  }
  return out;
}
